/**

 * Competition domain types (ADR-028).

 * Competition is the primary sporting aggregate — not Tournament/League roots.

 */



import { DEFAULT_SPORT } from "../sport/sportTypes";

import { DEFAULT_POINTS_TO_WIN } from "../../matchScoring";



/** @typedef {import('../sport/sportTypes').SportId} SportId */



/**

 * @typedef {'tournament_draw'|'league'|'ladder'|'challenge'|'club_night'|'friendly_series'} CompetitionKind

 */



/**

 * @typedef {'draft'|'published'|'registration_open'|'registration_closed'|'draw_published'|'in_progress'|'completed'|'archived'|'deleted'} CompetitionStatus

 */



/**

 * Entry — competitor in a Competition (player, pair, or team).

 * @typedef {Object} Entry

 * @property {string} entryId

 * @property {string} label  Display label (often former "team name")

 * @property {'player'|'pair'|'team'} entryType

 * @property {string[]} memberPlayerIds

 * @property {string[]} [memberRegistryEntryIds]  Room registry links when hosted in a Room

 * @property {number | null} [seed]

 */



/**

 * Fixture — scheduled pairing / bracket node.

 * @typedef {Object} Fixture

 * @property {string} fixtureId

 * @property {string | null} entryAId

 * @property {string | null} entryBId

 * @property {string | null} [labelA]  Denormalized for draw UI

 * @property {string | null} [labelB]

 * @property {'planned'|'scheduled'|'ready'|'live'|'completed'|'bye'|'walkover'|'postponed'|'cancelled'} status

 * @property {string | null} [winnerEntryId]

 * @property {number | null} [scoreA]

 * @property {number | null} [scoreB]

 * @property {string} [setsDetail]

 * @property {string | null} [matchId]  Live/completed Match id

 * @property {Object} [path]  Draw path for in-memory bracket helpers { round, match, group?, ko? }

 * @property {number | null} [matchNo]

 */



/**

 * Draw payload — format-specific structure produced by draw builders.

 * @typedef {Object} CompetitionDraw

 * @property {'knockout'|'round-robin'|'qualifier'} format

 * @property {Object[]} [rounds]

 * @property {Object[]} [groups]

 * @property {Object} [knockout]

 * @property {string[]} [teamOrder]

 * @property {number} [bracketSize]

 * @property {number} [byeCount]

 * @property {number} [topN]

 * @property {number} [numGroups]

 * @property {boolean} [groupsDone]

 */



/**

 * @typedef {Object} CompetitionVenue

 * @property {string} name

 * @property {string} [address]

 * @property {string} [city]

 * @property {string} [state]

 * @property {string} [country]

 */



/**

 * @typedef {Object} CompetitionDates

 * @property {number | null} [registrationOpenMs]

 * @property {number | null} [registrationCloseMs]

 * @property {number | null} startMs

 * @property {number | null} endMs

 */



/**

 * @typedef {Object} CompetitionDefaultMatchFormat

 * @property {number} setsToWin

 * @property {number} pointsToWin

 */



/**

 * @typedef {Object} CompetitionSettings

 * @property {'public'|'private'} visibility

 * @property {number} courtCount

 * @property {string} timeZone

 * @property {CompetitionDefaultMatchFormat} defaultMatchFormat

 */



/**

 * Competition aggregate (Firestore parent doc under competitions/{id}).

 * @typedef {Object} Competition

 * @property {string} competitionId

 * @property {SportId} sport

 * @property {CompetitionKind} kind

 * @property {CompetitionStatus} status

 * @property {string} createdBy

 * @property {string | null} organizationId

 * @property {string | null} eventId

 * @property {string | null} roomId  Hosting Room when started from a session

 * @property {string | null} name

 * @property {string} [description]

 * @property {string} [organizerUid]

 * @property {string} [organizerName]

 * @property {string | null} [bannerImageUrl]

 * @property {string | null} [logoUrl]

 * @property {CompetitionVenue | null} [venue]

 * @property {CompetitionDates | null} [dates]

 * @property {CompetitionSettings | null} [settings]

 * @property {string[]} [managerUids]

 * @property {string[]} [ownerIds]  Query helper: createdBy + managers

 * @property {import('../event/eventTypes').ManagedEventSummary[]} [managedEvents]

 * @property {import('./entryManagementTypes').ManagedCompetitionEntry[]} [managedEntries]
 * @property {import('./drawEngineTypes').ManagedDraw[]} [managedDraws]
 * @property {import('./tournamentOpsTypes').ManagedCourt[]} [managedCourts]
 * @property {object[]} [opsTimeline]

 * @property {number} [entryCount]

 * @property {Entry[]} entries

 * @property {CompetitionDraw | null} draw

 * @property {boolean} started

 * @property {number} setsToWin

 * @property {number} [createdAtMs]

 * @property {number} [updatedAtMs]

 */



export const COMPETITION_KINDS = Object.freeze({

  TOURNAMENT_DRAW: "tournament_draw",

  LEAGUE: "league",

  LADDER: "ladder",

  CHALLENGE: "challenge",

  CLUB_NIGHT: "club_night",

  FRIENDLY_SERIES: "friendly_series",

});



export const COMPETITION_STATUSES = Object.freeze({

  DRAFT: "draft",

  PUBLISHED: "published",

  REGISTRATION_OPEN: "registration_open",

  REGISTRATION_CLOSED: "registration_closed",

  /** @deprecated Prefer PUBLISHED for management; kept for room-draw compatibility */

  DRAW_PUBLISHED: "draw_published",

  IN_PROGRESS: "in_progress",

  COMPLETED: "completed",

  ARCHIVED: "archived",

  /** Soft-deleted — hidden from organizers lists/public; match history & stats remain. */
  DELETED: "deleted",

});



export const COMPETITION_VISIBILITY = Object.freeze({

  PUBLIC: "public",

  PRIVATE: "private",

});



export function newCompetitionId() {

  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {

    return `arn_${crypto.randomUUID()}`;

  }

  return `arn_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;

}



export function newEntryId() {

  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {

    return `ent_${crypto.randomUUID()}`;

  }

  return `ent_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;

}



export function defaultCompetitionSettings() {

  let timeZone = "UTC";

  try {

    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

  } catch {

    /* ignore */

  }

  return {

    visibility: COMPETITION_VISIBILITY.PUBLIC,

    courtCount: 1,

    timeZone,

    defaultMatchFormat: {

      setsToWin: 2,

      pointsToWin: DEFAULT_POINTS_TO_WIN,

    },

    /** League-table points for Round Robin standings (MVP badminton: win=2, loss=0). */
    standings: {

      pointsForWin: 2,

      pointsForLoss: 0,

      pointsForDraw: 1,

      qualifyTopN: 0,

    },

    /** Player self-registration (Phase 2.1) — not a new aggregate. */
    registration: {

      allowSelfRegister: true,

      requireApproval: true,

    },

  };

}



export function emptyCompetitionVenue() {

  return { name: "", address: "", city: "", state: "", country: "" };

}



export function emptyCompetitionDates() {

  return {

    registrationOpenMs: null,

    registrationCloseMs: null,

    startMs: null,

    endMs: null,

  };

}



/**

 * @param {Partial<Competition> & { competitionId: string, createdBy: string }} input

 * @returns {Competition}

 */

export function createCompetitionShell(input) {

  const createdBy = input.createdBy;

  const managerUids = Array.isArray(input.managerUids) ? input.managerUids : [];

  const settings = {

    ...defaultCompetitionSettings(),

    ...(input.settings || {}),

    defaultMatchFormat: {

      ...defaultCompetitionSettings().defaultMatchFormat,

      ...(input.settings?.defaultMatchFormat || {}),

    },

    standings: {

      ...defaultCompetitionSettings().standings,

      ...(input.settings?.standings || {}),

    },

    registration: {

      ...defaultCompetitionSettings().registration,

      ...(input.settings?.registration || {}),

    },

  };

  return {

    competitionId: input.competitionId,

    arenaId: input.arenaId || input.competitionId,

    sport: input.sport || DEFAULT_SPORT,

    sportId: input.sportId || input.sport || DEFAULT_SPORT,

    mode: input.mode || (input.kind === COMPETITION_KINDS.CLUB_NIGHT ? "casual" : "tournament"),

    enabledModules: Array.isArray(input.enabledModules)
      ? input.enabledModules
      : input.kind === COMPETITION_KINDS.CLUB_NIGHT
        ? []
        : ["tournament"],

    kind: input.kind || COMPETITION_KINDS.TOURNAMENT_DRAW,

    status: input.status || COMPETITION_STATUSES.DRAFT,

    createdBy,

    organizationId: input.organizationId ?? null,

    eventId: input.eventId ?? null,

    roomId: input.liveSessionId ?? input.roomId ?? null,

    liveSessionId: input.liveSessionId ?? input.roomId ?? null,

    name: input.name ?? null,

    description: input.description ?? "",

    organizerUid: input.organizerUid || createdBy,

    organizerName: input.organizerName ?? "",

    bannerImageUrl: input.bannerImageUrl ?? null,

    logoUrl: input.logoUrl ?? null,

    venue: input.venue ?? emptyCompetitionVenue(),

    dates: input.dates ?? emptyCompetitionDates(),

    settings,

    managerUids,

    ownerIds: input.ownerIds || [createdBy, ...managerUids].filter(Boolean),

    managedEvents: input.managedEvents || [],

    managedEntries: input.managedEntries || [],
    managedDraws: input.managedDraws || [],
    managedCourts: input.managedCourts || [],
    opsTimeline: input.opsTimeline || [],

    entryCount:
      input.entryCount ??
      (Array.isArray(input.managedEntries) ? input.managedEntries.length : null) ??
      (input.entries || []).length,

    entries: input.entries || [],

    ...(input.draw != null ? { draw: input.draw } : {}),

    started: Boolean(input.started),

    setsToWin: input.setsToWin ?? settings.defaultMatchFormat.setsToWin ?? 2,

    createdAtMs: input.createdAtMs ?? Date.now(),

    updatedAtMs: input.updatedAtMs ?? Date.now(),

  };

}



export {};


